import React, { useId } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { brand } from "../../theme/palette";
import { Skeleton } from "./AnalyticsUI";

// change vs the previous period; `null` means the previous period had no data
export const DeltaBadge = ({ change, invert = false }) => {
  if (change === undefined) return null;
  if (change === null) {
    return <span className="text-xs font-medium text-muted">No data in previous period</span>;
  }
  const flat = Math.abs(change) < 0.0005;
  const up = change > 0;
  const good = invert ? !up : up;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const tone = flat ? "bg-neutral-100 text-neutral-600" : good ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700";
  return (
    <span className={`inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums ${tone}`}>
      <Icon className="h-3.5 w-3.5" />
      {flat ? "0.0%" : `${Math.abs(change * 100).toFixed(1)}%`}
    </span>
  );
};

export const Sparkline = ({ data, dataKey, height = 40, color = brand[600], formatter }) => {
  const id = useId().replace(/:/g, "");
  if (!data?.length) return null;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`spark-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.22} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        {formatter && (
          <Tooltip
            cursor={false}
            content={({ active, payload }) =>
              active && payload?.length ? (
                <div className="rounded-md border border-line bg-white px-2 py-1 text-xs shadow-lg">
                  <span className="text-muted">{payload[0].payload.day}</span>{" "}
                  <span className="font-semibold tabular-nums text-fg">{formatter(payload[0].value)}</span>
                </div>
              ) : null
            }
          />
        )}
        <Area type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} fill={`url(#spark-${id})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// standard KPI tile
const KPICard = ({ label, value, unit, hint, change, invertChange, isLoading, spark, footer }) => (
  <div className="card flex flex-col p-4">
    <div className="flex items-start justify-between gap-2">
      <p className="text-13 font-medium text-neutral-600">{label}</p>
      {!isLoading && <DeltaBadge change={change} invert={invertChange} />}
    </div>
    {isLoading ? (
      <Skeleton className="mt-3 h-8 w-32" />
    ) : (
      <p className="mt-2 flex items-baseline gap-1.5 text-[26px] font-semibold leading-tight tracking-tight tabular-nums text-fg">
        {value}
        {unit && <span className="text-sm font-medium text-muted">{unit}</span>}
      </p>
    )}
    {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    {spark && !isLoading && <div className="mt-3 -mx-1">{spark}</div>}
    {footer && !isLoading && <div className="mt-auto pt-3">{footer}</div>}
  </div>
);

export default KPICard;
